import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';

const FaqsPage = () => {
    const [expandedIndex, setExpandedIndex] = useState(null);

    useEffect(() => {
        document.title = "FAQs | KAR – Kelowna Auto Rentals";
        document.querySelector('meta[name="description"]')?.setAttribute("content",
            "Answers to common questions about renting with KAR in Kelowna and Vancouver — age requirements, insurance, contactless pickup, cancellations, and more."
        );
    }, []);

    const faqs = [
        {
            question: 'What documents do I need to rent a car?',
            answer: 'A valid driver’s license and a credit card are required.',
        },
        {
            question: 'Can drivers under 25 rent a car?',
            answer: 'We accept drivers 25 and older. For special inquiries, please contact KAR customer service.',
        },
        {
            question: 'How does contactless pickup work?',
            answer: 'The vehicle is remotely unlocked via KAR customer service, allowing you to access it without any in-person interaction.',
        },
        {
            question: 'Is insurance included?',
            answer: 'Yes, insurance is included in the rental rates, which may vary depending on your driving record or experience. USA travelers can also use their personal rental car insurance if applicable.',
        },
        {
            question: 'What if my plans change?',
            answer: 'Bookings can be modified or canceled up to 24 hours before pickup. Late cancellations may incur fees.',
        },
        {
            question: 'How does vehicle drop-off work in Kelowna?',
            answer: 'KAR offers free contactless drop-off at Kelowna International Airport (YLW) and Orchard Park Mall using our 2-step secure check-in process. Your vehicle will be ready for pickup at your chosen location, eliminating the need to meet anyone in person. Simply follow the provided check-in instructions, and you’ll be on the road in minutes.',
        },
        {
            question: 'How do I contact KAR for support?',
            answer: 'Reach us at info@karrentals.ca or call +1 (250) 575-6041.',
        },
        {
            question: 'Are there any mileage limits on rentals?',
            answer: 'Mileage may vary depending on the vehicle, but all vehicles are guaranteed 200 km per day. Additional kilometers may incur fees.',
        },
        {
            question: 'What happens if I return the car late?',
            answer: 'Late returns may incur additional charges. Please contact KAR support if you expect any delays in returning the vehicle.',
        },
        {
            question: 'Can I add an additional driver to my rental?',
            answer: 'Yes, there is no additional charge for adding drivers. All additional drivers must meet the same rental requirements.',
        },
        {
            question: 'Do you offer long-term rentals?',
            answer: 'Yes, we offer long-term rentals (leasing) for local British Columbians. Contact us for more information on rates and availability.',
        },
        {
            question: 'What should I do if the vehicle breaks down?',
            answer: 'Our vehicles come with manufacturer roadside service. For incidents outside business hours, follow the roadside service instructions provided with the vehicle. During business hours (9 AM–5 PM PST, 7 days a week), please contact KAR customer service for assistance.',
        },
        {
            question: 'Can I take the rental across the border or to another province?',
            answer: 'We do not offer cross-border or inter-province rentals. If this is necessary, contact support, and we’ll do our best to accommodate your needs.',
        },
        {
            question: 'Is smoking or vaping allowed in the vehicles?',
            answer: 'Strictly prohibited. A cleaning fee will apply for violations.',
        },
        {
            question: 'What if I need a specific type of vehicle (e.g., SUV, electric)?',
            answer: 'We offer a range of vehicles to suit your needs. Browse availability during booking or contact us for special requests.',
        },
        {
            question: 'Are pets allowed in the rental cars?',
            answer: 'Pets over 20 lbs are not permitted. For smaller pets, the vehicle must be returned clean. Additional cleaning fees may apply at KAR\'s discretion.',
        },
        {
            question: 'How do I pay for tolls, tickets, or tows?',
            answer: 'All tolls, tickets, and tows incurred during your rental will be processed and charged to the credit card on file.',
        },
    ];

    const toggleFAQ = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    return (
        <Layout>
            <div id="faqs" className="container mx-auto p-4 my-12">
                <div
                    className="relative h-[66rem] bg-cover bg-center rounded-2xl overflow-hidden sm:hidden"
                    style={{ backgroundImage: 'url("/1.JPG")' }}
                >
                    <span className="relative text-3xl font-bold border-b-4 border-white flex text-center py-2 z-20">
                        Frequently Asked Questions
                    </span>
                    <div className="absolute inset-0 bg-black bg-opacity-75"></div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-11/12 mt-6 bg-opacity-75 bg-black rounded-2xl p-6 flex flex-row justify-between space-x-6"
                        style={{ maxHeight: '58rem', overflowY: 'auto' }}
                    >
                        <div className="flex-1 space-y-6 overflow-y-auto">
                            {faqs.map((faq, index) => (
                                <div key={index} className="border-b border-white pb-4">
                                    {/* Question */}
                                    <div
                                        className="flex justify-between items-center cursor-pointer"
                                        onClick={() => toggleFAQ(index)}
                                    >
                                        <h3 className="text-lg font-semibold text-white">
                                            {faq.question}
                                        </h3>
                                        <span className="text-white text-lg">
                                            {expandedIndex === index ? '−' : '+'}
                                        </span>
                                    </div>
                                    {/* Answer */}
                                    {expandedIndex === index && (
                                        <p className="text-white mt-2 text-lg transition-all duration-300">
                                            {faq.answer}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div
                    className="relative h-[52rem] bg-cover bg-center rounded-2xl overflow-hidden hidden sm:block"
                    style={{ backgroundImage: 'url("/ford.png")' }}
                >
                    <span className="text-4xl font-bold border-b-4 border-white flex justify-center py-2">
                        Frequently Asked Questions
                    </span>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-11/12 mt-6 bg-opacity-75 bg-black rounded-2xl p-6 flex flex-row justify-between space-x-6">
                        {/* Left Column */}
                        <div className="flex-1 space-y-6">
                            {faqs.slice(0, Math.ceil(faqs.length / 2)).map((faq, index) => (
                                <div key={index} className="border-b border-white pb-4">
                                    {/* Question */}
                                    <div
                                        className="flex justify-between items-center cursor-pointer"
                                        onClick={() => toggleFAQ(index)}
                                    >
                                        <h3 className="text-xl font-semibold text-white">
                                            {faq.question}
                                        </h3>
                                        <span className="text-white text-2xl">
                                            {expandedIndex === index ? '−' : '+'}
                                        </span>
                                    </div>
                                    {/* Answer */}
                                    {expandedIndex === index && (
                                        <p className="text-white mt-2 text-lg transition-all duration-300">
                                            {faq.answer}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Right Column */}
                        <div className="flex-1 space-y-6">
                            {faqs.slice(Math.ceil(faqs.length / 2)).map((faq, index) => (
                                <div
                                    key={index + Math.ceil(faqs.length / 2)}
                                    className="border-b border-white pb-4"
                                >
                                    {/* Question */}
                                    <div
                                        className="flex justify-between items-center cursor-pointer"
                                        onClick={() =>
                                            toggleFAQ(index + Math.ceil(faqs.length / 2))
                                        }
                                    >
                                        <h3 className="text-xl font-semibold text-white">
                                            {faq.question}
                                        </h3>
                                        <span className="text-white text-2xl">
                                            {expandedIndex ===
                                            index + Math.ceil(faqs.length / 2)
                                                ? '−'
                                                : '+'}
                                        </span>
                                    </div>
                                    {/* Answer */}
                                    {expandedIndex === index + Math.ceil(faqs.length / 2) && (
                                        <p className="text-white mt-2 text-lg transition-all duration-300">
                                            {faq.answer}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default FaqsPage;
